import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  VStack,
  HStack,
  Text,
  Select,
  useToast,
} from '@chakra-ui/react';
import LiquidGlassButton from './ui/LiquidGlassButton';
import { useLanguage } from '../i18n/LanguageContext';
import { LANGUAGE_LABELS, Language } from '../i18n/translations';
import { useWeatherRegion, WeatherRegionSetting } from '../hooks/useWeatherRegion';
import AddToHomeScreen from './AddToHomeScreen';
import CacheRefreshButton from './CacheRefreshButton';

const DEFAULT_PROFILES = ['Car Driver 1', 'Car Driver 2', 'Car Driver 3', 'Car Driver 4'];
const SELECTED_PROFILE_KEY = 'myCarParkedWhereSelectedProfile';
const CUSTOM_PROFILES_KEY = 'myCarParkedWhereCustomProfiles';
const HISTORY_STORAGE_KEY_PREFIX = 'myCarParkedWhereHistory_';

const LEGACY_PROFILE_SETS = [
  ['User1', 'User2', 'User3', 'KaiJian'],
  ['Car User', 'User2', 'User3', 'KaiJian'],
  ['Car Driver 1', 'Car Driver 2', 'Car Driver 3', 'KaiJian'],
];
function migrateProfilesIfNeeded() {
  const stored = localStorage.getItem(CUSTOM_PROFILES_KEY);
  if (!stored) return;
  try {
    const parsed: string[] = JSON.parse(stored);
    const isLegacy = LEGACY_PROFILE_SETS.some(
      set => set.length === parsed.length && set.every((p, i) => p === parsed[i])
    );
    if (isLegacy) {
      localStorage.setItem(CUSTOM_PROFILES_KEY, JSON.stringify(DEFAULT_PROFILES));
      localStorage.removeItem(SELECTED_PROFILE_KEY);
    }
  } catch {}
}
migrateProfilesIfNeeded();

interface ProfileIconProps {
  onProfileChange: (profile: string) => void;
  currentProfile: string;
  activeTab?: 'dashboard' | 'faq';
  onTabChange?: (tab: 'dashboard' | 'faq') => void;
}

const ProfileIcon: React.FC<ProfileIconProps> = ({ onProfileChange, currentProfile, onTabChange }) => {
  const { lang, setLang, t } = useLanguage();
  const { region: weatherRegion, setRegion: setWeatherRegion } = useWeatherRegion();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAddToHomeOpen, setIsAddToHomeOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [profiles, setProfiles] = useState<string[]>(() => {
    const saved = localStorage.getItem(CUSTOM_PROFILES_KEY);
    return saved ? JSON.parse(saved) : DEFAULT_PROFILES;
  });
  const [selectedProfile, setSelectedProfile] = useState(currentProfile);
  const [isEditing, setIsEditing] = useState(false);
  const [editingProfile, setEditingProfile] = useState('');
  const [tempName, setTempName] = useState('');

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isDropdownOpen]);

  const toast = useToast();

  useEffect(() => {
    localStorage.setItem(CUSTOM_PROFILES_KEY, JSON.stringify(profiles));
  }, [profiles]);

  const handleProfileSelect = (profile: string) => {
    setSelectedProfile(profile);
    onProfileChange(profile);
    localStorage.setItem(SELECTED_PROFILE_KEY, profile);
  };

  const startEditing = (profile: string) => {
    setIsEditing(true);
    setEditingProfile(profile);
    setTempName(profile);
  };

  const saveProfileName = () => {
    if (tempName.trim() && tempName.trim() !== editingProfile) {
      const oldName = editingProfile;
      const newName = tempName.trim();

      const newProfiles = profiles.map(p => p === oldName ? newName : p);
      setProfiles(newProfiles);

      if (selectedProfile === oldName) {
        setSelectedProfile(newName);
        onProfileChange(newName);
        localStorage.setItem(SELECTED_PROFILE_KEY, newName);
      }

      const oldHistoryKey = `${HISTORY_STORAGE_KEY_PREFIX}${oldName}`;
      const newHistoryKey = `${HISTORY_STORAGE_KEY_PREFIX}${newName}`;
      const historyData = localStorage.getItem(oldHistoryKey);
      if (historyData) {
        localStorage.setItem(newHistoryKey, historyData);
        localStorage.removeItem(oldHistoryKey);
      }

      const oldSessionKey = `myCarParkedWhereSession_${oldName}`;
      const newSessionKey = `myCarParkedWhereSession_${newName}`;
      const sessionData = localStorage.getItem(oldSessionKey);
      if (sessionData) {
        localStorage.setItem(newSessionKey, sessionData);
        localStorage.removeItem(oldSessionKey);
      }

      toast({
        position: 'top',
        title: `Renamed to "${newName}"`,
        status: 'success',
        duration: 2500,
      });
    }
    setIsEditing(false);
    setEditingProfile('');
    setTempName('');
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditingProfile('');
    setTempName('');
  };

  const getInitials = (name: string) => {
    // "Car Driver 1" → "CD", "Car Driver 2" → "CD2", etc.
    const match = name.match(/^(\S)\S*\s+(\S).*?(\d+)$/);
    if (match) {
      const [, first, second, num] = match;
      const initials = (first + second).toUpperCase();
      return num === '1' ? initials : initials + num;
    }
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 3);
  };

  return (
    <>
      {/* ── Profile icon + dropdown wrapper ── */}
      <Box position="relative" ref={dropdownRef} flexShrink={0} mb="-18px" mt={2} zIndex={10}>
        <LiquidGlassButton
          shape="circle"
          circleSize="74px"
          onClick={() => { setIsDropdownOpen(!isDropdownOpen); }}
          flexShrink={0}
          transform="none"
          _hover={{ transform: 'scale(1.06)' }}
          _focus={{ outline: 'none', boxShadow: 'none' }}
          _focusVisible={{ outline: 'none', boxShadow: 'none' }}
        >
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 11L6.5 6.5C6.8 5.6 7.65 5 8.6 5H15.4C16.35 5 17.2 5.6 17.5 6.5L19 11" stroke="rgba(255,255,255,0.9)" strokeWidth="1.6" strokeLinecap="round"/>
            <rect x="2" y="11" width="20" height="7" rx="2" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.9)" strokeWidth="1.6"/>
            <circle cx="6.5" cy="18" r="2" fill="rgba(255,255,255,0.9)"/>
            <circle cx="17.5" cy="18" r="2" fill="rgba(255,255,255,0.9)"/>
            <path d="M2 14H22" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
            <path d="M9 8.5H15" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2" strokeLinecap="round"/>
          </svg>
        </LiquidGlassButton>

        {/* ── Dark dropdown — 3 items ── */}
        {isDropdownOpen && (
          <Box
            position="absolute"
            top="72px"
            right={0}
            minW="220px"
            bg="rgba(8,11,22,0.96)"
            backdropFilter="blur(24px)"
            border="1px solid rgba(255,255,255,0.12)"
            borderRadius="14px"
            boxShadow="0 16px 48px rgba(0,0,0,0.7)"
            zIndex={2000}
            overflow="hidden"
            sx={{ WebkitBackdropFilter: 'blur(24px)' }}
          >
            {onTabChange && (
              <Box
                as="button" w="100%" textAlign="left"
                px={5} py={4} minH="52px"
                bg="transparent" border="none" outline="none" cursor="pointer"
                borderBottom="1px solid rgba(255,255,255,0.06)"
                _hover={{ bg: 'rgba(255,255,255,0.08)' }}
                _active={{ bg: 'rgba(255,255,255,0.12)' }}
                transition="background 0.12s"
                onClick={() => { onTabChange('dashboard'); setIsDropdownOpen(false); }}
              >
                <Text fontSize="15px" fontWeight="600" color="white">{t('home')}</Text>
              </Box>
            )}
            <Box
              as="button" w="100%" textAlign="left"
              px={5} py={4} minH="52px"
              bg="transparent" border="none" outline="none" cursor="pointer"
              borderBottom="1px solid rgba(255,255,255,0.06)"
              _hover={{ bg: 'rgba(255,255,255,0.08)' }}
              _active={{ bg: 'rgba(255,255,255,0.12)' }}
              transition="background 0.12s"
              onClick={() => { setIsDropdownOpen(false); setIsSettingsOpen(true); }}
            >
              <Text fontSize="15px" fontWeight="600" color="white">{t('settings')}</Text>
            </Box>
            {onTabChange && (
              <Box
                as="button" w="100%" textAlign="left"
                px={5} py={4} minH="52px"
                bg="transparent" border="none" outline="none" cursor="pointer"
                borderBottom="1px solid rgba(255,255,255,0.06)"
                _hover={{ bg: 'rgba(255,255,255,0.08)' }}
                _active={{ bg: 'rgba(255,255,255,0.12)' }}
                transition="background 0.12s"
                onClick={() => { onTabChange('faq'); setIsDropdownOpen(false); }}
              >
                <Text fontSize="15px" fontWeight="600" color="white">{t('faq')}</Text>
              </Box>
            )}
            <Box
              as="button" w="100%" textAlign="left"
              px={5} py={4} minH="52px"
              bg="transparent" border="none" outline="none" cursor="pointer"
              _hover={{ bg: 'rgba(255,255,255,0.08)' }}
              _active={{ bg: 'rgba(255,255,255,0.12)' }}
              transition="background 0.12s"
              onClick={() => { setIsDropdownOpen(false); setIsAddToHomeOpen(true); }}
            >
              <Text fontSize="15px" fontWeight="600" color="white">{t('addToHomeScreen')}</Text>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Add to Home Screen modal ── */}
      <AddToHomeScreen isOpen={isAddToHomeOpen} onClose={() => setIsAddToHomeOpen(false)} />

      {/* ── Settings modal (only opens from dropdown → Settings) ── */}
      <Modal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} isCentered>
        <ModalOverlay bg="rgba(0,0,0,0.6)" backdropFilter="blur(20px)" sx={{ WebkitBackdropFilter: 'blur(20px)' }} />
        <ModalContent
          bg="rgba(3,6,16,0.72)"
          backdropFilter="blur(40px) saturate(180%)"
          color="text.primary"
          mx={4}
          borderRadius="24px"
          border="1px solid rgba(255,255,255,0.08)"
          boxShadow="0 16px 56px rgba(0,0,0,0.5), 0 1px 0 inset rgba(255,255,255,0.07)"
          sx={{ WebkitBackdropFilter: 'blur(40px) saturate(180%)' }}
        >
          <ModalHeader
            fontSize="lg" fontWeight="700" color="white"
            borderBottom="1px solid rgba(255,255,255,0.08)"
            pb={4} letterSpacing="-0.02em"
          >
            {t('appSettings')}
          </ModalHeader>

          <Box
            as="button"
            position="absolute" top={3} right={3}
            w="40px" h="40px"
            display="flex" alignItems="center" justifyContent="center"
            borderRadius="10px"
            border="1px solid rgba(255,255,255,0.12)"
            bg="transparent" outline="none"
            cursor="pointer"
            _hover={{ borderColor: 'rgba(255,255,255,0.25)' }}
            _focus={{ outline: 'none', boxShadow: 'none' }}
            _focusVisible={{ outline: 'none', boxShadow: 'none' }}
            transition="border-color 0.15s"
            onClick={() => setIsSettingsOpen(false)}
          >
            <Text fontSize="16px" color="rgba(255,255,255,0.5)" lineHeight={1}>✕</Text>
          </Box>

          <ModalBody py={5}>
            <VStack spacing={5} align="stretch">
              {/* Active profile selector */}
              <Box>
                <Text mb={2} fontSize="11px" fontWeight="700" letterSpacing="0.1em"
                  textTransform="uppercase" color="rgba(255,255,255,0.5)">
                  {t('activeProfile')}
                </Text>
                <Select
                  value={selectedProfile}
                  onChange={(e) => handleProfileSelect(e.target.value)}
                  bg="rgba(255,255,255,0.05)"
                  borderColor="rgba(255,255,255,0.1)"
                  borderRadius="14px"
                  color="white"
                  fontWeight="600"
                  fontSize="14px"
                  h="48px"
                  _hover={{ borderColor: 'rgba(255,255,255,0.18)' }}
                  _focus={{ borderColor: 'rgba(255,255,255,0.4)', boxShadow: '0 0 0 3px rgba(255,255,255,0.08)' }}
                >
                  {profiles.map((profile) => (
                    <option key={profile} value={profile}
                      style={{ backgroundColor: '#0a0e1a' }}>
                      {profile}
                    </option>
                  ))}
                </Select>
              </Box>

              {/* Profile list */}
              <Box>
                <Text mb={3} fontSize="11px" fontWeight="700" letterSpacing="0.1em"
                  textTransform="uppercase" color="rgba(255,255,255,0.5)">
                  {t('manageProfiles')}
                </Text>
                <VStack spacing={2} align="stretch">
                  {profiles.map((profile) => (
                    <Box
                      key={profile}
                      border="1px solid rgba(255,255,255,0.08)"
                      borderRadius="14px"
                      px={4} py={3}
                      display="flex" alignItems="center" gap={3}
                    >
                      {isEditing && editingProfile === profile ? (
                        <>
                          <Input
                            value={tempName}
                            onChange={(e) => setTempName(e.target.value)}
                            size="sm"
                            bg="rgba(255,255,255,0.05)"
                            borderColor="rgba(255,255,255,0.1)"
                            color="white"
                            borderRadius="10px"
                            flex="1" h="36px"
                            _focus={{ borderColor: 'rgba(255,255,255,0.4)', boxShadow: '0 0 0 2px rgba(255,255,255,0.08)' }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') saveProfileName();
                              if (e.key === 'Escape') cancelEditing();
                            }}
                            autoFocus
                          />
                          <Text as="button" fontSize="13px" fontWeight="700" color="#4ADE80"
                            cursor="pointer" onClick={saveProfileName} px={2}>✓</Text>
                          <Text as="button" fontSize="13px" color="rgba(255,255,255,0.5)"
                            cursor="pointer" onClick={cancelEditing} px={2}>✕</Text>
                        </>
                      ) : (
                        <HStack justify="space-between" w="100%">
                          <HStack spacing={3}>
                            <Box
                              w="28px" h="28px" borderRadius="full"
                              bg="rgba(255,255,255,0.1)" border="1px solid rgba(255,255,255,0.2)"
                              display="flex" alignItems="center" justifyContent="center" flexShrink={0}
                            >
                              <Text fontSize="10px" fontWeight="800" color="#e0e4ea">
                                {getInitials(profile)}
                              </Text>
                            </Box>
                            <Text fontSize="14px" color="white" fontWeight="600">{profile}</Text>
                          </HStack>
                          <Text
                            as="button" fontSize="12px" fontWeight="600"
                            color="rgba(255,255,255,0.45)" cursor="pointer"
                            onClick={() => startEditing(profile)}
                            _hover={{ color: 'rgba(255,255,255,0.7)' }}
                            transition="color 0.15s"
                          >
                            {t('edit')}
                          </Text>
                        </HStack>
                      )}
                    </Box>
                  ))}
                </VStack>
              </Box>

              {/* Language selector */}
              <Box>
                <Text mb={2} fontSize="11px" fontWeight="700" letterSpacing="0.1em"
                  textTransform="uppercase" color="rgba(255,255,255,0.5)">
                  {t('language')}
                </Text>
                <Select
                  value={lang}
                  onChange={(e) => setLang(e.target.value as Language)}
                  bg="rgba(255,255,255,0.05)"
                  borderColor="rgba(255,255,255,0.1)"
                  borderRadius="14px"
                  color="white"
                  fontWeight="600"
                  fontSize="14px"
                  h="48px"
                  _hover={{ borderColor: 'rgba(255,255,255,0.18)' }}
                  _focus={{ borderColor: 'rgba(255,255,255,0.3)', boxShadow: '0 0 0 3px rgba(255,255,255,0.06)' }}
                >
                  {(Object.keys(LANGUAGE_LABELS) as Language[]).map((code) => (
                    <option key={code} value={code}
                      style={{ backgroundColor: '#0a0e1a' }}>
                      {LANGUAGE_LABELS[code]}
                    </option>
                  ))}
                </Select>
              </Box>

              {/* Weather region selector */}
              <Box>
                <Text mb={2} fontSize="11px" fontWeight="700" letterSpacing="0.1em"
                  textTransform="uppercase" color="rgba(255,255,255,0.5)">
                  {t('weatherRegion')}
                </Text>
                <Select
                  value={weatherRegion}
                  onChange={(e) => setWeatherRegion(e.target.value as WeatherRegionSetting)}
                  bg="rgba(255,255,255,0.05)"
                  borderColor="rgba(255,255,255,0.1)"
                  borderRadius="14px"
                  color="white"
                  fontWeight="600"
                  fontSize="14px"
                  h="48px"
                  _hover={{ borderColor: 'rgba(255,255,255,0.18)' }}
                  _focus={{ borderColor: 'rgba(255,255,255,0.3)', boxShadow: '0 0 0 3px rgba(255,255,255,0.06)' }}
                >
                  <option value="sg" style={{ backgroundColor: '#0a0e1a' }}>🇸🇬 Singapore</option>
                  <option value="sg+jb" style={{ backgroundColor: '#0a0e1a' }}>🇸🇬 Singapore + 🇲🇾 Johor Bahru</option>
                </Select>
              </Box>

              {/* Cache refresh — for users with stuck UI */}
              <Box>
                <Text mb={2} fontSize="11px" fontWeight="700" letterSpacing="0.1em"
                  textTransform="uppercase" color="rgba(255,255,255,0.5)">
                  Troubleshoot
                </Text>
                <HStack
                  justify="space-between"
                  align="center"
                  px={4}
                  py={3}
                  border="1px solid rgba(255,255,255,0.08)"
                  borderRadius="14px"
                >
                  <Text fontSize="13px" color="rgba(255,255,255,0.65)">
                    Having UI issues? Clear app cache
                  </Text>
                  <CacheRefreshButton />
                </HStack>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px solid rgba(255,255,255,0.06)" pt={4}>
            <Box
              as="button"
              px={5} py={2}
              borderRadius="10px"
              border="1px solid rgba(255,255,255,0.12)"
              bg="transparent" outline="none"
              cursor="pointer"
              _hover={{ borderColor: 'rgba(255,255,255,0.25)' }}
              _focus={{ outline: 'none', boxShadow: 'none' }}
              _focusVisible={{ outline: 'none', boxShadow: 'none' }}
              transition="border-color 0.15s"
              onClick={() => setIsSettingsOpen(false)}
            >
              <Text fontSize="14px" fontWeight="600" color="rgba(255,255,255,0.5)">
                {t('close')}
              </Text>
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ProfileIcon;
